import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { isLightColor } from './themeDefaults';
import { isSubdomain, getSubdomainUsername } from '../../utils/subdomainUtils';
import { customDomains } from '../../config/customDomains';
import { resolvePageUrl } from '../../utils/pageUtils';

/**
 * WebsiteHeader - Pure render component for the persistent profile header.
 * All data (coach, library, profile) is passed in as props from ProfileSiteLayout.
 * No internal fetches — single source of truth from the layout.
 */
const WebsiteHeader = ({
  profileName,
  profileLogo,
  username,
  hasCoachingBlock,
  hasLibraryContent = false,
  headerNavLinks = [],
  headerNavStyle = 'buttons',
  headerStyle: headerStyleProp = {},
  theme = {},
  coach,
  previewContext = null,
}) => {
  // When previewContext is set, the header is rendered inside the editor's Preview
  // mode. Nav links that would normally route to live pages instead switch the
  // editor's active page via previewContext.onPageChange so the user can hop
  // between pages without triggering the unsaved-changes guard.
  const isInPreview = !!previewContext;
  const previewPageChange = (pageSlug) => (e) => {
    if (e) e.preventDefault();
    previewContext?.onPageChange?.(pageSlug);
  };
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSectionsOpen, setIsSectionsOpen] = useState(false);

  // Detect if we're on a custom domain or subdomain — home should be "/" not "/me/username"
  const isOnOwnDomain = useMemo(() => {
    const hostname = window.location.hostname.replace(/^www\./, '');
    if (customDomains[hostname]) return true;
    if (isSubdomain() && getSubdomainUsername()?.toLowerCase() === username?.toLowerCase()) return true;
    return false;
  }, [username]);

  // Page context detection — precise path matching
  const currentPath = location.pathname;
  const isOnCoachLanding = currentPath === `/coach/${username}` || currentPath === `/me/${username}/coach` || (isOnOwnDomain && currentPath === '/coach');
  const isOnDashboardPage = currentPath === `/coach/${username}/dashboard` || currentPath === `/me/${username}/coach/dashboard` || (isOnOwnDomain && currentPath === '/coach/dashboard');
  const isOnLibraryPage = currentPath.startsWith('/library/') || currentPath === `/me/${username}/library` || currentPath.startsWith(`/me/${username}/library/`);
  const isOnHomePage = currentPath === '/' || currentPath === '/donate'
    || currentPath === `/me/${username}` || currentPath === `/me/${username}/donate`;

  // Detect active custom page from URL — matches /me/:username/:pageSlug or /:pageSlug on own domain
  const activePageSlug = useMemo(() => {
    const mePrefix = `/me/${username}/`;
    if (currentPath.startsWith(mePrefix)) {
      const rest = currentPath.slice(mePrefix.length);
      // Only match if it's a simple slug (no further slashes) and not a known route
      if (rest && !rest.includes('/') && !['donate', 'blog', 'coach', 'library'].includes(rest)) return rest;
    }
    if (isOnOwnDomain && currentPath.startsWith('/') && !currentPath.includes('/', 1)) {
      const rest = currentPath.slice(1);
      if (rest && !['donate', 'blog', 'coach', 'library', 'dashboard', 'admin', 'auth'].includes(rest)) return rest;
    }
    return null;
  }, [currentPath, username, isOnOwnDomain]);

  // Extract theme colors with fallbacks, then apply header-specific overrides
  const colors = theme.colors || {};
  const pageBackground = headerStyleProp.sectionBackground || colors.pageBackground || '#0d0d0d';
  const headingColor = headerStyleProp.headingColor || colors.headingColor || '#ffffff';
  const mutedColor = headerStyleProp.bodyColor || colors.mutedColor || '#94a3b8';
  const accent = headerStyleProp.buttonColor || colors.accent || '#749555';
  const accentGradient = headerStyleProp.buttonColor ? null : colors.accentGradient;
  const preset = theme.preset || 'dark';
  const isDark = preset === 'dark';

  // Button text color: header override → theme override → auto-detect from accent
  const autoButtonTextColor = isLightColor(accent) ? '#1a1a2e' : '#ffffff';
  const buttonTextColor = headerStyleProp.buttonTextColor || colors.buttonTextColor || autoButtonTextColor;

  // Header-specific font overrides
  const headerNameFont = headerStyleProp.headingFont || null;
  const headerNavFont = headerStyleProp.bodyFont || null;

  // Preload GroupChess lazy chunks on home page so navigation is instant
  useEffect(() => {
    if (!isOnHomePage) return;
    const preload = () => {
      import('../../components/groupchess/GroupChessLayout').catch(() => {});
      import('../../pages/groupchess/GroupChessLanding').catch(() => {});
      import('../../pages/groupchess/LibraryPage').catch(() => {});
      import('../../pages/groupchess/GroupChessDashboard').catch(() => {});
    };
    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(preload, { timeout: 3000 });
      return () => cancelIdleCallback(id);
    } else {
      const id = setTimeout(preload, 2000);
      return () => clearTimeout(id);
    }
  }, [isOnHomePage]);

  // Check ownership
  const isOwner = currentUser && coach && coach.userId === currentUser.uid;

  // Build URLs — all internal, all use React Router
  // On custom domain/subdomain, home is "/" (root). Otherwise "/me/username".
  const homeUrl = isOnOwnDomain ? '/' : (username ? `/me/${username}` : '/');
  const coachUrl = isOnOwnDomain ? '/coach' : (username ? `/me/${username}/coach` : '/coach');
  const dashboardUrl = isOnOwnDomain ? '/coach/dashboard' : (username ? `/me/${username}/coach/dashboard` : '/coach/dashboard');
  const adminUrl = '/dashboard/coach';
  const libraryUrl = isOnOwnDomain ? '/library' : (username ? `/me/${username}/library` : '/library');
  const signInUrl = `/auth?context=student&redirect=${encodeURIComponent(dashboardUrl)}`;

  // Whether coaching OR library features exist (controls auth button visibility)
  const hasAuthFeatures = hasCoachingBlock || hasLibraryContent;

  // Logo — always goes to /me/username (client-side navigation)
  const handleLogoClick = (e) => {
    if (currentPath === homeUrl) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Close mobile menu and sections dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isMenuOpen && !e.target.closest('.wbh-container')) {
        setIsMenuOpen(false);
      }
      if (isSectionsOpen && !e.target.closest('.sections-dropdown-wrapper')) {
        setIsSectionsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMenuOpen, isSectionsOpen]);

  // Close mobile menu and sections dropdown on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setIsSectionsOpen(false);
  }, [currentPath]);

  const toggleMenu = (e) => {
    e.stopPropagation();
    setIsMenuOpen(!isMenuOpen);
  };

  // Get initials for fallback logo
  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Handle nav link click — scroll to block on home page, navigate home first if on another page
  const scrollToBlock = (blockId) => {
    const el = document.getElementById(`block-${blockId}`);
    if (el) {
      const headerHeight = 64;
      const top = el.getBoundingClientRect().top + window.scrollY - headerHeight;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const handleNavClick = (e, blockId) => {
    e.preventDefault();
    setIsMenuOpen(false);
    setIsSectionsOpen(false);

    if (isOnHomePage) {
      scrollToBlock(blockId);
    } else {
      // Navigate home, then scroll after the page renders
      navigate(homeUrl);
      setTimeout(() => scrollToBlock(blockId), 150);
    }
  };

  // Resolve a page nav link to its URL
  const getPageLinkUrl = (pageSlug) => resolvePageUrl(pageSlug, username, isOnOwnDomain);

  // In Preview mode, page-slug nav items render as buttons that switch the
  // editor's active page instead of <Link>s that route to the live site.
  const PageNavItem = ({ pageSlug, className, style, onClick, children }) => {
    if (isInPreview) {
      return (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            previewContext.onPageChange(pageSlug);
            if (onClick) onClick(e);
          }}
          className={className}
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', ...style }}
        >
          {children}
        </button>
      );
    }
    return (
      <Link to={getPageLinkUrl(pageSlug)} onClick={onClick} className={className} style={style}>
        {children}
      </Link>
    );
  };
  const isPageLinkActive = (pageSlug) => {
    if (isInPreview) {
      const cur = previewContext.currentPageSlug;
      if (pageSlug === '__home') return cur == null;
      return cur === pageSlug;
    }
    if (pageSlug === '__home') return isOnHomePage;
    if (pageSlug === '__coaching') return isOnCoachLanding;
    if (pageSlug === '__library') return isOnLibraryPage;
    if (pageSlug === '__blog') return currentPath.includes('/blog');
    return activePageSlug === pageSlug;
  };

  // Filter out __coaching/__library from nav links — those are auto-rendered separately
  const filteredNavLinks = headerNavLinks.filter(link => link.pageSlug !== '__coaching' && link.pageSlug !== '__library');
  // Split: regular links (Home, custom pages, scroll-to, external) render first, then Blog
  const regularNavLinks = filteredNavLinks.filter(link => link.pageSlug !== '__blog');
  const blogNavLinks = filteredNavLinks.filter(link => link.pageSlug === '__blog');

  // Determine if we should show navigation elements
  const hasNavLinks = filteredNavLinks.length > 0 || blogNavLinks.length > 0;
  const showMobileMenu = hasNavLinks || hasCoachingBlock || hasLibraryContent;

  // Get button background style (gradient or solid)
  const getButtonBackground = () => {
    if (accentGradient) {
      return accentGradient;
    }
    return `linear-gradient(135deg, ${accent} 0%, ${adjustColor(accent, -15)} 100%)`;
  };

  const brandContent = (
    <>
      {profileLogo ? (
        <img src={profileLogo} alt={profileName} className="header-logo" />
      ) : (
        <div className="header-logo-fallback" style={{ background: getButtonBackground() }}>
          {getInitials(profileName)}
        </div>
      )}
      <span className="header-name" style={{
        color: headingColor,
        ...(headerNameFont ? { fontFamily: `'${headerNameFont}', var(--theme-font-heading, inherit)` } : {}),
      }}>
        {profileName || 'Profile'}
      </span>
    </>
  );

  return (
    <>
      <header className="website-header">
        <div className="wbh-container">
          {/* Logo + Name — links to home (preview: switches editor's active page) */}
          {isInPreview ? (
            <button
              type="button"
              onClick={previewPageChange(null)}
              className="header-brand header-brand-link"
              style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
            >
              {brandContent}
            </button>
          ) : (
            <Link
              to={homeUrl}
              onClick={handleLogoClick}
              className="header-brand header-brand-link"
            >
              {brandContent}
            </Link>
          )}

          {/* Desktop Navigation */}
          {(hasNavLinks || hasCoachingBlock || hasLibraryContent) && (
            <nav className="header-nav desktop-nav">
              {/* Section nav links — visible on all pages, scroll to block on home or navigate home first */}
              {headerNavStyle === 'dropdown' && regularNavLinks.length > 0 ? (
                <div className="sections-dropdown-wrapper" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="sections-dropdown-btn"
                    style={{ color: mutedColor }}
                    onClick={() => setIsSectionsOpen(!isSectionsOpen)}
                  >
                    Sections
                    <ChevronDown
                      size={16}
                      style={{
                        transition: 'transform 0.2s ease',
                        transform: isSectionsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      }}
                    />
                  </button>
                  {isSectionsOpen && (
                    <div className="sections-dropdown-menu" style={{
                      background: isDark ? adjustColor(pageBackground, 8) : adjustColor(pageBackground, -5),
                      borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                    }}>
                      {regularNavLinks.map((link) => (
                        link.pageSlug ? (
                          <PageNavItem
                            key={`page-${link.pageSlug}`}
                            pageSlug={link.pageSlug}
                            className={`sections-dropdown-item ${isPageLinkActive(link.pageSlug) ? 'sections-dropdown-active' : ''}`}
                            style={{ color: isPageLinkActive(link.pageSlug) ? headingColor : mutedColor }}
                            onClick={() => setIsSectionsOpen(false)}
                          >
                            {link.label}
                          </PageNavItem>
                        ) : link.url ? (
                          <Link
                            key={link.blockId || link.label}
                            to={link.url}
                            className="sections-dropdown-item"
                            style={{ color: mutedColor }}
                            onClick={() => setIsSectionsOpen(false)}
                          >
                            {link.label}
                          </Link>
                        ) : (
                          <a
                            key={link.blockId}
                            href={`#block-${link.blockId}`}
                            onClick={(e) => handleNavClick(e, link.blockId)}
                            className="sections-dropdown-item"
                            style={{ color: mutedColor }}
                          >
                            {link.label}
                          </a>
                        )
                      ))}
                    </div>
                  )}
                </div>
              ) : regularNavLinks.map((link) => (
                link.pageSlug ? (
                  <PageNavItem
                    key={`page-${link.pageSlug}`}
                    pageSlug={link.pageSlug}
                    className={`header-nav-link ${isPageLinkActive(link.pageSlug) ? 'header-nav-active' : ''}`}
                    style={{ color: isPageLinkActive(link.pageSlug) ? headingColor : mutedColor }}
                  >
                    {link.label}
                  </PageNavItem>
                ) : link.url ? (
                  <Link
                    key={link.blockId || link.label}
                    to={link.url}
                    className="header-nav-link"
                    style={{ color: mutedColor }}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.blockId}
                    href={`#block-${link.blockId}`}
                    onClick={(e) => handleNavClick(e, link.blockId)}
                    className="header-nav-link"
                    style={{ color: mutedColor }}
                  >
                    {link.label}
                  </a>
                )
              ))}

              {/* Blog — after custom pages, before coaching/library */}
              {blogNavLinks.map((link) => (
                <PageNavItem
                  key={`page-${link.pageSlug}`}
                  pageSlug={link.pageSlug}
                  className={`header-nav-link ${isPageLinkActive(link.pageSlug) ? 'header-nav-active' : ''}`}
                  style={{ color: isPageLinkActive(link.pageSlug) ? headingColor : mutedColor }}
                >
                  {link.label}
                </PageNavItem>
              ))}

              {/* Coaching — visible when coaching exists */}
              {hasCoachingBlock && (
                isInPreview ? (
                  <button
                    type="button"
                    onClick={previewPageChange('__coaching')}
                    className={`header-nav-link ${isPageLinkActive('__coaching') ? 'header-nav-active' : ''}`}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: isPageLinkActive('__coaching') ? headingColor : mutedColor }}
                  >
                    Coaching
                  </button>
                ) : (
                  <Link
                    to={coachUrl}
                    className={`header-nav-link ${isOnCoachLanding ? 'header-nav-active' : ''}`}
                    style={{ color: isOnCoachLanding ? headingColor : mutedColor }}
                  >
                    Coaching
                  </Link>
                )
              )}

              {/* Library — visible to all visitors when content exists */}
              {hasLibraryContent && (
                isInPreview ? (
                  <button
                    type="button"
                    onClick={previewPageChange('__library')}
                    className={`header-nav-link ${isPageLinkActive('__library') ? 'header-nav-active' : ''}`}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: isPageLinkActive('__library') ? headingColor : mutedColor }}
                  >
                    Library
                  </button>
                ) : (
                  <Link
                    to={libraryUrl}
                    className={`header-nav-link ${isOnLibraryPage ? 'header-nav-active' : ''}`}
                    style={{ color: isOnLibraryPage ? headingColor : mutedColor }}
                  >
                    Library
                  </Link>
                )
              )}

              {/* Dashboard — hidden in editor preview (irrelevant offline) */}
              {!isInPreview && hasAuthFeatures && currentUser && (
                <Link
                  to={dashboardUrl}
                  className={`${isOwner ? 'header-nav-link' : 'header-btn header-btn-primary'} ${isOnDashboardPage ? (isOwner ? 'header-nav-active' : 'header-btn-active') : ''}`}
                  style={isOwner
                    ? { color: isOnDashboardPage ? headingColor : mutedColor }
                    : (!isOnDashboardPage ? { background: getButtonBackground() } : undefined)
                  }
                >
                  Dashboard
                </Link>
              )}

              {/* Sign In — hidden in editor preview */}
              {!isInPreview && hasAuthFeatures && !currentUser && (
                <Link
                  to={signInUrl}
                  className="header-btn header-btn-primary"
                  style={{ background: getButtonBackground() }}
                >
                  Sign In
                </Link>
              )}

              {/* Admin — hidden in editor preview */}
              {!isInPreview && hasAuthFeatures && isOwner && (
                <Link
                  to={adminUrl}
                  className="header-btn header-btn-primary"
                  style={{ background: getButtonBackground() }}
                >
                  Admin
                </Link>
              )}
            </nav>
          )}

          {/* Mobile Menu Button */}
          {showMobileMenu && (
            <button
              className="mobile-menu-btn"
              onClick={toggleMenu}
              aria-label="Toggle menu"
              style={{ color: headingColor }}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          )}
        </div>

        {/* Mobile Dropdown */}
        {showMobileMenu && (
          <div className={`mobile-dropdown ${isMenuOpen ? 'open' : ''}`}>
            <nav className="mobile-nav">
              {/* Regular nav links — Home, custom pages, scroll-to, external */}
              {regularNavLinks.map((link) => (
                link.pageSlug ? (
                  <PageNavItem
                    key={`page-${link.pageSlug}`}
                    pageSlug={link.pageSlug}
                    className={`mobile-nav-link ${isPageLinkActive(link.pageSlug) ? 'mobile-nav-active' : ''}`}
                    onClick={() => setIsMenuOpen(false)}
                    style={{ color: headingColor }}
                  >
                    {link.label}
                  </PageNavItem>
                ) : link.url ? (
                  <Link
                    key={link.blockId || link.label}
                    to={link.url}
                    className="mobile-nav-link"
                    onClick={() => setIsMenuOpen(false)}
                    style={{ color: headingColor }}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.blockId}
                    href={`#block-${link.blockId}`}
                    onClick={(e) => handleNavClick(e, link.blockId)}
                    className="mobile-nav-link"
                    style={{ color: headingColor }}
                  >
                    {link.label}
                  </a>
                )
              ))}

              {/* Blog — after custom pages, before coaching/library */}
              {blogNavLinks.map((link) => (
                <PageNavItem
                  key={`page-${link.pageSlug}`}
                  pageSlug={link.pageSlug}
                  className={`mobile-nav-link ${isPageLinkActive(link.pageSlug) ? 'mobile-nav-active' : ''}`}
                  onClick={() => setIsMenuOpen(false)}
                  style={{ color: headingColor }}
                >
                  {link.label}
                </PageNavItem>
              ))}

              {/* Coaching */}
              {hasCoachingBlock && (
                isInPreview ? (
                  <button
                    type="button"
                    onClick={(e) => { previewPageChange('__coaching')(e); setIsMenuOpen(false); }}
                    className={`mobile-nav-link ${isPageLinkActive('__coaching') ? 'mobile-nav-active' : ''}`}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', color: headingColor }}
                  >
                    Coaching
                  </button>
                ) : (
                  <Link
                    to={coachUrl}
                    className={`mobile-nav-link ${isOnCoachLanding ? 'mobile-nav-active' : ''}`}
                    onClick={() => setIsMenuOpen(false)}
                    style={{ color: headingColor }}
                  >
                    Coaching
                  </Link>
                )
              )}

              {/* Library — visible to all visitors */}
              {hasLibraryContent && (
                isInPreview ? (
                  <button
                    type="button"
                    onClick={(e) => { previewPageChange('__library')(e); setIsMenuOpen(false); }}
                    className={`mobile-nav-link ${isPageLinkActive('__library') ? 'mobile-nav-active' : ''}`}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', color: headingColor }}
                  >
                    Library
                  </button>
                ) : (
                  <Link
                    to={libraryUrl}
                    className={`mobile-nav-link ${isOnLibraryPage ? 'mobile-nav-active' : ''}`}
                    onClick={() => setIsMenuOpen(false)}
                    style={{ color: headingColor }}
                  >
                    Library
                  </Link>
                )
              )}

              {/* Dashboard — hidden in editor preview */}
              {!isInPreview && hasAuthFeatures && currentUser && (
                <Link
                  to={dashboardUrl}
                  className={`mobile-nav-link ${isOnDashboardPage ? 'mobile-nav-active' : ''}`}
                  onClick={() => setIsMenuOpen(false)}
                  style={{ color: headingColor }}
                >
                  Dashboard
                </Link>
              )}

              {/* Sign In — hidden in editor preview */}
              {!isInPreview && hasAuthFeatures && !currentUser && (
                <Link
                  to={signInUrl}
                  className="mobile-nav-link"
                  onClick={() => setIsMenuOpen(false)}
                  style={{ color: headingColor }}
                >
                  Sign In
                </Link>
              )}

              {/* Admin — hidden in editor preview */}
              {!isInPreview && hasAuthFeatures && isOwner && (
                <Link
                  to={adminUrl}
                  className="mobile-nav-link"
                  onClick={() => setIsMenuOpen(false)}
                  style={{ color: headingColor }}
                >
                  Admin
                </Link>
              )}
            </nav>
          </div>
        )}
      </header>

      <style>{`
        .website-header {
          position: sticky;
          top: 0;
          height: 64px;
          background: ${pageBackground} !important;
          background-color: ${pageBackground} !important;
          z-index: 40;
        }

        .wbh-container {
          max-width: 1200px;
          margin: 0 auto;
          height: 100%;
          padding: 0 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        /* Brand */
        .header-brand {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        a.header-brand-link {
          text-decoration: none;
          transition: opacity 0.2s ease;
        }

        a.header-brand-link:hover {
          opacity: 0.8;
        }

        .header-logo {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          object-fit: cover;
        }

        .header-logo-fallback {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.85rem;
          font-weight: 700;
          color: white;
        }

        .header-name {
          font-size: 1.1rem;
          font-weight: 600;
          font-family: var(--theme-font-heading, inherit);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 50vw;
        }

        /* Desktop Navigation */
        .header-nav {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .desktop-nav {
          display: flex;
        }

        .header-btn {
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: 600;
          transition: all 0.2s ease;
          text-decoration: none;
        }

        .header-btn-primary {
          color: ${buttonTextColor};
          border: none;
        }

        .header-btn-primary:hover {
          box-shadow: 0 4px 12px ${accent}66;
          transform: translateY(-1px);
        }

        /* Nav Links */
        .header-nav-link {
          padding: 8px 12px;
          font-size: 0.9rem;
          font-weight: 500;
          text-decoration: none;
          transition: color 0.2s ease;
          border-radius: 6px;
          ${headerNavFont ? `font-family: '${headerNavFont}', var(--theme-font-body, inherit);` : ''}
        }

        .header-nav-link:hover {
          color: ${headingColor} !important;
          background: ${isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
        }

        .header-nav-active {
          background: ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'};
        }

        .header-btn-active {
          background: transparent;
          color: ${headingColor};
          border: 1px solid ${isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'};
        }

        /* Sections Dropdown */
        .sections-dropdown-wrapper {
          position: relative;
        }

        .sections-dropdown-btn {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 8px 12px;
          font-size: 0.9rem;
          font-weight: 500;
          background: transparent;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          transition: color 0.2s ease, background 0.2s ease;
        }

        .sections-dropdown-btn:hover {
          color: ${headingColor} !important;
          background: ${isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
        }

        .sections-dropdown-menu {
          position: absolute;
          top: calc(100% + 4px);
          left: 0;
          min-width: 180px;
          border: 1px solid;
          border-radius: 8px;
          padding: 4px;
          z-index: 50;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
          animation: sectionsDropdownFadeIn 0.15s ease;
        }

        .sections-dropdown-item {
          display: block;
          padding: 8px 12px;
          font-size: 0.9rem;
          font-weight: 500;
          text-decoration: none;
          border-radius: 6px;
          transition: color 0.2s ease, background 0.2s ease;
          cursor: pointer;
        }

        .sections-dropdown-item:hover {
          color: ${headingColor} !important;
          background: ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'};
        }

        @keyframes sectionsDropdownFadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Mobile Menu Button */
        .mobile-menu-btn {
          display: none;
          background: transparent;
          border: none;
          padding: 8px;
          cursor: pointer;
          border-radius: 8px;
          transition: background 0.2s ease;
        }

        .mobile-menu-btn:hover {
          background: ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
        }

        /* Mobile Dropdown */
        .mobile-dropdown {
          display: none;
          background: ${adjustColor(pageBackground, isDark ? 3 : -3)};
          border-bottom: 1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'};
          padding: 0 24px;
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.2s ease, padding 0.2s ease;
        }

        .mobile-dropdown.open {
          max-height: 500px;
          padding: 16px 24px;
        }

        .mobile-nav {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .mobile-nav-link {
          display: block;
          padding: 12px 16px;
          text-decoration: none;
          font-size: 1rem;
          font-weight: 500;
          border-radius: 8px;
          transition: background 0.2s ease;
        }

        .mobile-nav-link:hover {
          background: ${accent}33;
        }

        .mobile-nav-active {
          background: ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'};
          font-weight: 600;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .desktop-nav {
            display: none;
          }

          .mobile-menu-btn {
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .mobile-dropdown {
            display: block;
          }

          .header-name {
            max-width: calc(100vw - 120px);
          }
        }

        @media (max-width: 480px) {
          .wbh-container {
            padding: 0 16px;
          }

          .header-name {
            max-width: calc(100vw - 110px);
            font-size: 1rem;
          }

          .header-logo,
          .header-logo-fallback {
            width: 32px;
            height: 32px;
          }
        }
      `}</style>
    </>
  );
};

// Helper function to adjust color brightness
function adjustColor(hex, percent) {
  if (!hex || !hex.startsWith('#')) return hex;
  const num = parseInt(hex.slice(1), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, Math.min(255, (num >> 16) + amt));
  const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00FF) + amt));
  const B = Math.max(0, Math.min(255, (num & 0x0000FF) + amt));
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
}

export default WebsiteHeader;
